<template>
  <v-row class="p-0 m-0">
    <div class="col-6 pb-0 mb-0">
      <v-combobox :label="$t('NewCalendarItem.StartTime')" v-model="selectedStartTime" :items="startTimesList"
        menu-props="auto" item-text="timeDisplay" item-value="timeValue" :readonly="readonly" :disabled="disabled"
        :placeholder="placeholder" :append-icon="readonly ? '' : 'mdi-menu-down'" variant="underlined"
        @change="onUpdateSelectedStartTime" @keydown="handleKeydown">
      </v-combobox>
      <div v-if="errors.isStartTimeInvalid" class="error-msg">{{ $t('NewCalendarItem.StartTimeInvalid') }}</div>
    </div>
    <div class="col-6 pb-0 mb-0">
      <v-combobox :label="$t('NewCalendarItem.EndTime')" v-model="selectedEndTime" :items="endTimesList" menu-props="auto"
        item-text="timeDisplay" item-value="timeValue" :readonly="readonly" :disabled="disabled"
        :placeholder="placeholder" :append-icon="readonly ? '' : 'mdi-menu-down'" variant="underlined"
        @change="onUpdateSelectedEndTime" @keydown="handleKeydown">
      </v-combobox>
      <div v-if="errors.isEndTimeBeforeStartTime" class="error-msg">{{ $t('NewCalendarItem.EndTimeBeforeStartTime') }}
      </div>
      <div v-if="errors.isEndTimeInvalid" class="error-msg">{{ $t('NewCalendarItem.EndTimeInvalid') }}</div>
    </div>
  </v-row>
</template>

<script>
import moment from "moment/moment";

export default {
  components: {},
  props: {
    startTime: String,
    endTime: String,
    readonly: Boolean,
    disabled: Boolean,
    placeholder: String,
    round: Boolean,
    keydown: Boolean
  },
  data() {
    return {
      errors: {
        isEndTimeBeforeStartTime: false,
        isStartTimeInvalid: false,
        isEndTimeInvalid: false,
      },
      selectedStartTime: null,
      selectedEndTime: null,
      startTimesList: [],
      endTimesList: [],
    }
  },

  computed: {
    facility() {
      return this.$store.state.authModule.currentFacilityData
    },
    isAmPmFormat() {
      const format = this.facility.timeFormat.split(' ')[1] || ''
      return format === 'A'
    },
    timeFormat() {
      return this.isAmPmFormat ? 'hh:mm A' : 'HH:mm'
    },
    getDateForStartTime(){
      const todayDate = moment().format("YYYY-MM-DD")

      const dateTimeMoment = moment(
          `${todayDate} ${this.selectedStartTime === '24:00'? '00:00': this.selectedStartTime}`,
          `YYYY-MM-DD ${this.timeFormat}`,
          true
        );
        
        if (this.selectedStartTime === "24:00" || this.selectedStartTime === "00:00") {
          dateTimeMoment.subtract(1, 'day');
        }

      return dateTimeMoment
    }
  },

  watch: {
    selectedStartTime(value) {
      this.isStartTimeValid()
      this.emitChanges()
    },
    selectedEndTime(value) {
      this.isEndTimeValid()
      this.emitChanges()
    },
  },

  created() {
    const _startTime = this.startTime ? this.startTime : moment().format(this.timeFormat)
    const _endTime = this.endTime ? this.endTime : moment().format(this.timeFormat)

    if (this.round) {
      this.selectedStartTime = this.roundTo15Minutes(_startTime)
      this.selectedEndTime = this.roundTo15Minutes(_endTime)
    }
    else {
      this.selectedStartTime = _startTime
      this.selectedEndTime = _endTime
    }

    if(!this.endTime) {
      this.onUpdateSelectedStartTime({timeValue: _startTime})
    }

    this.startTimesList = this.generateTimesForStartTimeList(this.roundTo15Minutes(_startTime), this.selectedEndTime)
    this.endTimesList = this.generateTimesForEndTimeList(this.roundTo15Minutes(_startTime), this.selectedEndTime)
  },

  methods: {
    generateTimesForStartTimeList(startTime, endTime) {
      return this.generateTimesList(startTime, endTime, this.isAmPmFormat);
    },

    generateTimesForEndTimeList(startTime, endTime) {
      console.log(this.generateTimesList(startTime, endTime, this.isAmPmFormat))
      return this.generateTimesList(startTime, endTime, this.isAmPmFormat).map((timeObj, index) => {
        const timeDiff = timeObj.timeDiff;
        const currentTimeObj = this.startTimesList[index];

        return {
          timeValue: timeObj.timeValue,
          timeDisplay: `${timeObj.timeValue} ${timeDiff ? `(${timeDiff})` : ''}`,
          timeDiff: timeDiff,
          diffFromStartTime: currentTimeObj?.timeDiff
        };
      });
    },

    generateTimesList(startTime, endTime, isAmPm) {

      const times = [];
      const [startHours, startMins] = startTime.split(':');
      const startMinutes = startMins.split(' ')[0];
      const startAmPm = startTime.includes('AM') ? 'AM' : 'PM'

      let startHoursNumber = Number(startHours);
      if (isAmPm) {
        if (startTime.includes('AM') && startHoursNumber === 12) {
          startHoursNumber -= 12;
        } else if (startTime.includes('PM') && startHoursNumber < 12) {
          startHoursNumber += 12;
        }
      }

      const startTimeMinutes = startHoursNumber * 60 + Number(startMinutes);

      for (let i = 0; i < 97; i++) {
        const timeMinutes = i * 15;
        const generatedHours = Math.floor(timeMinutes / 60);
        const generatedMinutes = (timeMinutes % 60).toString().padStart(2, '0');

        let time;
        if (isAmPm) {
          const amPm = generatedHours < 12 ? 'AM' : 'PM';
          const formattedHours = (generatedHours % 12 || 12).toString().padStart(2, '0');
          time = `${formattedHours}:${generatedMinutes} ${amPm}`
        } else {
          const formattedHours = generatedHours.toString().padStart(2, '0');
          time = `${formattedHours}:${generatedMinutes}`
        }

        const timeDiffMinutes = timeMinutes - startTimeMinutes;

        let timeDiff;
        if (timeDiffMinutes <= 0) {
          timeDiff = null;
        } else {
          const timeDiffHours = Math.floor(timeDiffMinutes / 60);
          const timeDiffMinutesRemainder = timeDiffMinutes % 60;
          timeDiff = `${timeDiffHours.toString().padStart(2, '0')}:${timeDiffMinutesRemainder.toString().padStart(2, '0')}`
        }

        times.push({
          timeValue: time,
          timeDisplay: time,
          timeDiff: timeDiff
        });
      }

      return times;
    },

    roundTo15Minutes(time) {
      const format = ["hh:mm A","h:mm A","HH:mm"];
      const parsedTime = moment(time, format, true);
      if (!parsedTime.isValid()) {
        return time;
      }
      const remainder = parsedTime.minutes() % 15;
      if (remainder !== 0) {
        parsedTime.add(15 - remainder, "minutes");
      }
      return parsedTime.format(this.timeFormat);
    },

    roundTo15MinutesPrev(time) {
      const timeComponents = time.split(/:|(?=[ap]m)/i);
      const hours = parseInt(timeComponents[0], 10);
      const minutes = parseInt(timeComponents[1], 10);
      const amPm = timeComponents[2];

      // Round the minutes to the nearest 15-minute interval
      let roundedMinutes = Math.ceil(minutes / 15) * 15;
      let roundedHours = hours;

      if (roundedMinutes === 60) {
        roundedHours += 1;
        roundedMinutes = 0;
      }

      // Check if AM/PM format is present
      if (amPm) {
        if (amPm.toLowerCase() === 'PM' && roundedHours < 12) {
          roundedHours += 12;
        } else if (amPm.toLowerCase() === 'AM' && roundedHours === 12) {
          roundedHours = 0;
        }
      }

      // Format the hours and minutes
      const formattedHours = roundedHours.toString().padStart(2, '0');
      const formattedMinutes = roundedMinutes.toString().padStart(2, '0');

      // Determine the format based on the presence of AM/PM
      if (amPm) {
        return `${formattedHours}:${formattedMinutes} ${amPm}`;
      } else {
        return `${formattedHours}:${formattedMinutes}`;
      }
    },

    onUpdateSelectedStartTime(selectedItem) {
      // set value of a selected item or value that user manually entered
      this.selectedStartTime = selectedItem?.timeValue || this.fixTime(selectedItem) || null;

      if (!this.isStartTimeValid()) {
        this.selectedStartTime = null;
        return;
      }

      if (!this.selectedStartTime && this.round) {
        this.selectedStartTime = this.roundTo15Minutes(this.selectedStartTime);
      }

      this.clearEndTimeValidationState();

      const startTimeMoment = this.getDateForStartTime

      // Check if hour is 23, i.e. from 23:00 up to 23:59
      if (startTimeMoment.hour() === 23) {
        this.selectedEndTime = this.isAmPmFormat ? '11:59 PM' : '23:59';
      } else {
        // Otherwise add 1 hour
        const formattedEndTime = moment(startTimeMoment).add(1, 'hour');
        this.selectedEndTime = formattedEndTime.format(this.timeFormat);
      }

      this.endTimesList = this.generateTimesForEndTimeList(this.selectedStartTime, this.endTime, this.isAmPmFormat)
    },

    onUpdateSelectedEndTime(selectedItem) {
      //set value of a selected item or value that user manually entered
      this.selectedEndTime = selectedItem?.timeValue || this.fixTime(selectedItem) || null;
    },

    isStartTimeValid() {
      if (!moment(this.selectedStartTime, this.timeFormat, true).isValid()) {
        this.errors.isStartTimeInvalid = true
        return false
      }

      this.errors.isStartTimeInvalid = false

      return true
    },

    isEndTimeValid() {
      if (!moment(this.selectedEndTime, this.timeFormat, true).isValid()) {
        this.errors.isEndTimeInvalid = true
        return false
      }

      if (this.selectedEndTime) {
        const startTime = this.getDateForStartTime
        const endTime = moment(this.selectedEndTime, this.timeFormat);

        if (endTime.isBefore(startTime)) {
          this.errors.isEndTimeBeforeStartTime = true;
          return false;
        }
      }

      this.errors.isEndTimeInvalid = false
      this.errors.isEndTimeBeforeStartTime = false

      return true
    },

    clearEndTimeValidationState() {
      this.errors.isEndTimeInvalid = false
      this.errors.isEndTimeBeforeStartTime = false
    },

    //fix manually entered time
    fixTime(time) {
      if (!time) return null

      const timeParts = time.split(' ');
      const timeWithoutSpace = timeParts.join('');

      if (timeWithoutSpace.match(/^\d{1,2}:\d{2}(AM|PM)$/i)) {
        const formattedTime = time.replace(/(\d{1,2}:\d{2})(AM|PM)/i, '$1 $2');
        return formattedTime;
      }

      return time;
    },

    async emitChanges() {
      this.$emit('onTimeRangeChange', { startTime: this.selectedStartTime, endTime: this.selectedEndTime, isValid: (this.isStartTimeValid() && this.isEndTimeValid()) })
    },

    handleKeydown(event) {
      if (!this.keydown) {
        event.preventDefault();
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
